import { useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  CREATE_CARD_EPHEMERAL_KEY,
  GET_CARD_EPHEMERAL_KEY,
} from "../graphql/card";
import { useLazyQuery, useMutation } from "@apollo/client";
import { renderHighnoteCardFields } from "utils/cards/highnoteCardFields";

export interface StyleOptions {
  base?: {
    iconColor?: string;
    color?: string;
    fontWeight?: string;
    fontFamily?: string;
    fontSize?: string;
    lineHeight?: string;
    letterSpacing?: string;
    fontSmoothing?: string;
    ":-webkit-autofill"?: {
      color?: string;
    };
    "::placeholder"?: {
      color?: string;
    };
  };
  invalid?: {
    iconColor?: string;
    color?: string;
  };
}

export interface CardElementOptions {
  style: StyleOptions;
}

interface CardEphemeralKeyData {
  issuerId: string;
  accountId?: string | null;
  key: { id?: string | null; secret?: string | null };
}

const useIssuingElement = () => {
  const { t } = useTranslation();

  const [createCardEphemeralKey, { loading: createLoading }] = useMutation<{
    createCardEphemeralKey: { token: string };
  }>(CREATE_CARD_EPHEMERAL_KEY, {
    onError: (error) => {
      toast.error(error?.message || t("Please try again!"));
    },
  });

  const [getCardEphemeralKey, { loading: getLoading, error }] = useLazyQuery<{
    getCardEphemeralKey: { data: CardEphemeralKeyData };
  }>(GET_CARD_EPHEMERAL_KEY, {
    fetchPolicy: "network-only",
  });

  const [currentCardId, setCurrentCardId] = useState("");
  const [isCardDataDisplayed, setIsCardDataDisplayed] = useState(false);
  // Each reveal takes a number; one whose number is no longer current belongs to a card the user left.
  const revealRef = useRef(0);

  // A reveal still in flight when the card UI unmounts stops at its next await.
  useEffect(() => {
    const reveals = revealRef;
    return () => {
      reveals.current += 1;
    };
  }, []);

  const renderStripeCard = async (
    { issuerId, accountId, key }: CardEphemeralKeyData,
    cardId: string,
    style: CardElementOptions,
    isCurrent: () => boolean,
  ) => {
    const stripe = await loadStripe(
      process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string,
      { stripeAccount: accountId ?? undefined },
    );
    if (!stripe) {
      throw new Error("Could not connect to provider. Please try again later.");
    }

    const { nonce } = await stripe.createEphemeralKeyNonce({
      issuingCard: issuerId,
    });
    if (!isCurrent()) return;

    const elements = stripe.elements();
    const shown = {
      issuingCard: issuerId,
      nonce,
      ephemeralKeySecret: key.secret ?? undefined,
      style: style.style,
    };
    elements
      .create("issuingCardNumberDisplay", shown)
      .mount(`#card-number-${cardId}`);
    elements
      .create("issuingCardCvcDisplay", shown)
      .mount(`#card-cvc-${cardId}`);
    elements
      .create("issuingCardExpiryDisplay", shown)
      .mount(`#card-exp-${cardId}`);

    const cardNumberCopy = elements.create("issuingCardCopyButton", {
      toCopy: "number",
      style: style.style,
    });
    cardNumberCopy.mount(`#card-number-copy-${cardId}`);
    cardNumberCopy.on("click", () => {
      toast.success(t("Card number copied to clipboard"));
    });
  };

  const renderHighnoteCard = (
    { issuerId, key }: CardEphemeralKeyData,
    cardId: string,
    style: CardElementOptions,
  ) => {
    // Only the six declarations Highnote allows; `fontSmoothing` is Stripe's.
    const { fontSmoothing, iconColor, ...base } = style.style.base ?? {};
    const fieldStyles = Object.fromEntries(
      Object.entries(base).filter(([, value]) => Boolean(value)),
    );

    return renderHighnoteCardFields({
      clientToken: key.secret ?? "",
      paymentCardId: issuerId,
      selectors: {
        cardNumber: `#card-number-${cardId}`,
        cvv: `#card-cvc-${cardId}`,
        expirationDate: `#card-exp-${cardId}`,
      },
      styles: fieldStyles,
      t,
    });
  };

  const resetCardData = () => {
    revealRef.current += 1;
    setIsCardDataDisplayed(false);
    setCurrentCardId("");
  };

  /** Resolves true once the card shows, false when its reveal failed or was left. */
  async function handleViewCard(
    cardId: string,
    style: CardElementOptions,
  ): Promise<boolean> {
    if (cardId === currentCardId || isCardDataDisplayed) return true;
    const reveal = ++revealRef.current;
    const isCurrent = () => reveal === revealRef.current;
    setCurrentCardId(cardId);

    try {
      const { data: created } = await createCardEphemeralKey({
        variables: { id: cardId },
      });
      const token = created?.createCardEphemeralKey.token;
      if (!token || !isCurrent()) return false;

      const { data } = await getCardEphemeralKey({ variables: { token } });
      if (!isCurrent()) return false;
      const cardKey = data?.getCardEphemeralKey.data;
      if (!cardKey) throw new Error("The card key did not arrive.");

      if (cardKey.key.id) {
        await renderStripeCard(cardKey, cardId, style, isCurrent);
      } else if (cardKey.key.secret) {
        await renderHighnoteCard(cardKey, cardId, style);
      } else {
        throw new Error("The card key has no secret.");
      }
      if (!isCurrent()) return false;
      setIsCardDataDisplayed(true);
      return true;
    } catch (error) {
      if (isCurrent()) {
        console.error(error);
        toast.error(t("SomethingWentWrongDescriptionWithSupport"));
      }
      return false;
    } finally {
      if (isCurrent()) setCurrentCardId("");
    }
  }

  return {
    handleViewCard,
    loading: createLoading || getLoading,
    error,
    resetCardData,
  };
};

export default useIssuingElement;
